import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';

import { ensureUsercentricsV3Loaded } from '@xing-com/cookie-consent-usercentrics';
import { GetContextualConsentAttributionClicksDocument } from '@xing-com/crate-jobs-graphql';
import { useExperiment } from '@xing-com/hub';

import {
  ATTRIBUTION_LOOKBACK_SECONDS,
  CONTEXTUAL_CONSENT_EXPERIMENT,
  getEligibleMetaClickId,
  hasContextualMeasurementOptOutCookie,
  META_PARTNER_NAME,
  META_PIXEL_SERVICE_ID,
} from './contextual-consent-eligibility';
import { useTrackContextualConsentEligibility } from './use-track-contextual-consent-eligibility';

const USERCENTRICS_TIMEOUT_MS = 6000;

type ConsentState = 'loading' | 'granted' | 'denied' | 'error';

export type ContextualConsentEligibility = {
  isEligible: boolean;
  isLoading: boolean;
  metaClickId?: string;
};

const withTimeout = async <T>(promise: Promise<T>): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const timeout = new Promise<never>((_resolve, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error('Usercentrics consent check timed out')),
      USERCENTRICS_TIMEOUT_MS
    );
  });

  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  }
};

const getMetaPixelConsent = async (): Promise<boolean> => {
  await ensureUsercentricsV3Loaded({ suppressCmpDisplay: true });

  if (!window.__ucCmp) {
    throw new Error('Usercentrics is unavailable');
  }

  const consentDetails = await window.__ucCmp.getConsentDetails();
  return consentDetails.services[META_PIXEL_SERVICE_ID]?.consent.given ?? false;
};

const hasOptedOut = (): boolean =>
  typeof document !== 'undefined' &&
  hasContextualMeasurementOptOutCookie(document.cookie);

export const useContextualConsentEligibility =
  (): ContextualConsentEligibility => {
    const isExperimentVariant =
      useExperiment(CONTEXTUAL_CONSENT_EXPERIMENT) === 'B';
    const optOutCookieSet = hasOptedOut();
    const shouldCheckEligibility = isExperimentVariant && !optOutCookieSet;
    const [consentState, setConsentState] = useState<ConsentState>('loading');

    const { data, error, loading } = useQuery(
      GetContextualConsentAttributionClicksDocument,
      {
        fetchPolicy: 'no-cache',
        skip: !shouldCheckEligibility,
        variables: {
          lookbackWindowSeconds: ATTRIBUTION_LOOKBACK_SECONDS,
          mode: 'latest',
          partnerName: META_PARTNER_NAME,
        },
      }
    );

    useEffect(() => {
      if (!shouldCheckEligibility) {
        return;
      }

      let isActive = true;
      setConsentState('loading');

      withTimeout(getMetaPixelConsent())
        .then((hasConsent) => {
          if (isActive) {
            setConsentState(hasConsent ? 'granted' : 'denied');
          }
        })
        .catch(() => {
          if (isActive) {
            setConsentState('error');
          }
        });

      return () => {
        isActive = false;
      };
    }, [shouldCheckEligibility]);

    const metaClickId = shouldCheckEligibility
      ? getEligibleMetaClickId(data?.attributionClicks?.clicks ?? [])
      : undefined;
    const isLoading = loading || consentState === 'loading';

    useTrackContextualConsentEligibility({
      experimentEnabled: isExperimentVariant,
      optOutCookieSet,
      checksSkipped: !shouldCheckEligibility,
      isLoading,
      recentFacebookClick: !!metaClickId,
      metaConsentGiven: consentState === 'granted',
      attributionError: !!error,
      consentError: consentState === 'error',
    });

    if (!shouldCheckEligibility) {
      return { isEligible: false, isLoading: false };
    }

    const isEligible =
      !isLoading && !error && consentState === 'denied' && !!metaClickId;

    return {
      isEligible,
      isLoading,
      ...(isEligible && { metaClickId }),
    };
  };
